import { formatAppDateTime } from "../shared/dateUtils"
import closeIconUrl from "./icons/close.svg"
import type {
  StayGuestHospitalityCategory,
  StayGuestHospitalityEntry,
  StayGuestHospitalityTarget,
} from "./stayData"

type GuestHospitalityListProps = {
  category: StayGuestHospitalityCategory
  entries: readonly StayGuestHospitalityEntry[]
  isSaving: boolean
  onRemoveNote: (entry: StayGuestHospitalityEntry) => void
  targets: readonly StayGuestHospitalityTarget[]
}

export function GuestHospitalityList({
  category,
  entries,
  isSaving,
  onRemoveNote,
  targets,
}: GuestHospitalityListProps) {
  const targetGroups = targets
    .map((target) => ({
      target,
      entries: entries.filter((entry) => entry.category === category && (entry.guest_id || "") === target.id),
    }))
    .filter((group) => group.entries.length)

  if (!targetGroups.length) {
    return null
  }

  return (
    <div className="flex flex-col gap-4">
      {targetGroups.map((group) => (
        <section className="flex flex-col gap-2" key={`${category}-${group.target.id || "whole-party"}`}>
          <h4 className="text-xs font-semibold uppercase leading-4 tracking-[0.72px] text-[#404040]">
            {group.target.label}
          </h4>
          <div className="flex flex-col gap-2">
            {group.entries.map((entry) => (
              <HospitalityEntry
                entry={entry}
                isSaving={isSaving}
                key={entry.id}
                onRemoveNote={onRemoveNote}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

function HospitalityEntry({
  entry,
  isSaving,
  onRemoveNote,
}: {
  entry: StayGuestHospitalityEntry
  isSaving: boolean
  onRemoveNote: (entry: StayGuestHospitalityEntry) => void
}) {
  const meta = entry.source === "profile"
    ? "From guest profile"
    : (
      <>
        <span>Captured by {entry.created_by || "Unknown"}</span>
        {entry.created_at ? (
          <>
            <span aria-hidden="true"> · </span>
            <span>{formatAppDateTime(entry.created_at)}</span>
          </>
        ) : null}
      </>
    )

  return (
    <article className="group flex min-w-0 items-center gap-2 rounded-[4px] border border-[#d4d4d4] bg-[#fafafa] px-3 py-2 hover:bg-[#f5f5f5]">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-normal leading-[18px] text-black">{entry.text}</p>
        <p className="mt-1 text-xs font-normal leading-normal text-[#737373]">{meta}</p>
      </div>
      {entry.source === "note" ? (
        <button
          aria-label={`Remove hospitality note: ${entry.text}`}
          className="grid size-4 shrink-0 place-items-center text-base leading-none text-[#737373] opacity-0 transition-opacity hover:text-black focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] group-hover:opacity-100 disabled:cursor-not-allowed disabled:text-[#a3a3a3]"
          disabled={isSaving}
          onClick={() => onRemoveNote(entry)}
          type="button"
        >
          <img alt="" className="block size-[8.17px]" src={closeIconUrl} />
        </button>
      ) : null}
    </article>
  )
}
